import type { NotificationDto } from './types';
import { EffectsRegistry } from './EffectsRegistry';
import { ConfettiAdapter } from './ConfettiAdapter';

const KIND_TO_VARIANT: Record<string, string> = {
  success: 'gold',     // gold/celebratory for accept
  danger:  'dark',     // dark/quiet for decline
  info:    'outline',  // outline/neutral for tertiary
  neutral: 'solid',
};

const CTA_VARIANTS = new Set(['solid', 'outline', 'gold', 'neon', 'magenta', 'glass', 'dark', 'pulse']);

export interface RenderResult {
  /** Why the popup closed — drives upstream bookkeeping (dismiss vs action). */
  reason: 'closed' | 'cta' | 'action' | 'autohide';
  /** For required popups, the id of the action the user picked. */
  actionId?: string;
}

/**
 * Builds the popup DOM client-side from the JSON payload, fires confetti, and
 * resolves with how the popup closed.
 *
 * The DOM shape mirrors the Smarty templates (default.tpl / prize.tpl / ...) — keep them
 * structurally aligned so server-rendered repeat-view (history page) looks identical.
 */
export class Renderer {
  private hostEl: HTMLElement;

  constructor(hostEl?: HTMLElement) {
    this.hostEl = hostEl ?? this.ensureHost();
  }

  /** Render the popup. Resolves with the reason it closed. */
  async show(item: NotificationDto): Promise<RenderResult> {
    const wrap = this.buildWrapper(item);
    const card = this.buildCard(item);
    wrap.appendChild(card);
    this.hostEl.appendChild(wrap);

    ConfettiAdapter.fire(item.confetti, card);

    return new Promise<RenderResult>((resolve) => {
      let resolved = false;
      const finish = (result: RenderResult): void => {
        if (resolved) return;
        resolved = true;
        if (timer !== null) window.clearTimeout(timer);
        wrap.remove();
        resolve(result);
      };

      // Close button — present only when not required.
      card.querySelector<HTMLElement>('[data-popup-close]')?.addEventListener('click', () => finish({ reason: 'closed' }));
      // Single CTA — non-required popups.
      card.querySelector<HTMLElement>('[data-popup-confirm]')?.addEventListener('click', () => finish({ reason: 'cta' }));
      // Multi-action buttons — required popups; each carries data-action-id.
      card.querySelectorAll<HTMLElement>('[data-action-id]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          finish({ reason: 'action', actionId: btn.dataset.actionId });
        });
      });

      // Auto-hide only applies to non-required popups.
      const timer =
        !item.required && item.auto_hide && item.auto_hide > 0
          ? window.setTimeout(() => finish({ reason: 'autohide' }), item.auto_hide * 1000)
          : null;
    });
  }

  /** Wrapper holds optional modal backdrop (required popups) and the card itself. */
  private buildWrapper(item: NotificationDto): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'popup-wrap' + (item.required ? ' popup-wrap--modal' : '');
    return wrap;
  }

  private buildCard(item: NotificationDto): HTMLElement {
    const enter  = EffectsRegistry.enter(item.enter);
    const center = EffectsRegistry.center(item.center);
    const hasActions = Array.isArray(item.actions) && item.actions.length > 0;

    const el = document.createElement('div');
    el.className =
      `popup popup--style-${this.cssSafe(item.style)} popup-enter--${enter}` +
      (item.required ? ' popup--required' : '');
    el.dataset.popupUuid = item.uuid;
    el.dataset.confetti  = item.confetti;

    el.innerHTML = `
      ${item.required ? '' : '<button type="button" class="popup__close" data-popup-close aria-label="Close">×</button>'}
      ${item.icon && item.icon !== 'none' ? `
        <div class="popup__icon popup-center--${center}">
          <span class="popup__icon-sprite popup__icon-sprite--${this.cssSafe(item.icon)}"></span>
        </div>` : ''}
      ${item.title ? `<h3 class="popup__title">${escapeHtml(item.title)}</h3>` : ''}
      ${item.body  ? `<p class="popup__body">${escapeHtml(item.body)}</p>`   : ''}
      ${hasActions ? this.renderActions(item.actions) : this.renderCta(item)}
    `;
    return el;
  }

  /** Render the multi-action button row used by required popups. */
  private renderActions(actions: NotificationDto['actions']): string {
    const buttons = actions.map((a) => {
      const variant = KIND_TO_VARIANT[a.kind] ?? 'solid';
      const variantCls = CTA_VARIANTS.has(variant) && variant !== 'solid' ? ` popup__cta--${variant}` : '';
      return `<button type="button" class="popup__cta popup__cta--kind-${this.cssSafe(a.kind)}${variantCls}"
                      data-action-id="${escapeAttr(a.id)}">${escapeHtml(a.label)}</button>`;
    }).join('');
    return `<div class="popup__actions">${buttons}</div>`;
  }

  /**
   * Render the CTA as an anchor when a URL is provided (the default — "Играть" → "/"),
   * otherwise as a plain button. The anchor still carries data-popup-confirm so the
   * outer click handler can mark the popup dismissed before navigation kicks in.
   */
  private renderCta(item: NotificationDto): string {
    if (!item.cta) return '';
    const variant = this.ctaVariantClass(item.cta_style);
    const cls = `popup__cta${variant}`;
    if (item.cta_url) {
      const safeUrl = this.sanitizeUrl(item.cta_url);
      return `<a class="${cls}" href="${safeUrl}" data-popup-confirm>${escapeHtml(item.cta)}</a>`;
    }
    return `<button type="button" class="${cls}" data-popup-confirm>${escapeHtml(item.cta)}</button>`;
  }

  private ctaVariantClass(name: string): string {
    if (!name || name === 'solid' || !CTA_VARIANTS.has(name)) return '';
    return ' popup__cta--' + name;
  }

  private sanitizeUrl(url: string): string {
    if (url.startsWith('/') && !url.startsWith('//')) return url;
    try {
      const u = new URL(url, window.location.origin);
      return u.origin === window.location.origin ? u.toString() : '/';
    } catch {
      return '/';
    }
  }

  private ensureHost(): HTMLElement {
    let host = document.querySelector<HTMLElement>('.popup-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'popup-host';
      document.body.appendChild(host);
    }
    return host;
  }

  private cssSafe(s: string): string {
    return s.replace(/[^a-z0-9_-]/gi, '');
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
